import React, { useEffect, useMemo, useState } from "react";
import { assignRunCell, getClassSubjectsByWeek } from "./happyschoolApi";

const DAYS = ["Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];
const khoiOf = (name) => (String(name || "").match(/^\d+/) || [""])[0];
const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/gi, "d").toLowerCase();
const NOT_TEACHING_NAMES = new Set(["Hoàng Thị Hoa"]); // người trường xác nhận không được phân công dạy

const FILTERS = [
  { key: "subject", label: "Cùng môn đã chọn" },
  { key: "class", label: "Dạy lớp này" },
  { key: "khoi", label: "Cùng khối" },
  { key: "all", label: "Tất cả GV rảnh" },
];

// Dialog admin ĐIỀN 1 Ô TRỐNG của LỚP (nhấn đúp ô trống ở "TKB cho GV & Học sinh — Toàn trường"): chọn MÔN rồi chọn
// GIÁO VIÊN rảnh đúng giờ. Ghi thẳng vào TKB đang xem, không ghi vào xếp tay.
const HappySchoolCellFillDialog = ({ schoolId, runId, weekId, target, grid, classes, subjects, teachers, isBusyAt, onClose, onSaved }) => {
  const { classId, day, period } = target;
  const className = (classes.find((c) => c.id === classId) || {}).name || classId;
  const khoi = khoiOf(className);
  const subjectById = useMemo(() => Object.fromEntries(subjects.map((s) => [s.id, s.name])), [subjects]);
  const classById = useMemo(() => Object.fromEntries(classes.map((c) => [c.id, c.name])), [classes]);
  const [subjectSel, setSubjectSel] = useState("");
  const [filter, setFilter] = useState("subject");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState("");
  const [reason, setReason] = useState("");
  const [needMap, setNeedMap] = useState(null);
  const [pccmMap, setPccmMap] = useState({}); // subjectId -> teacherId được PCCM cho lớp này (tuần này)
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!schoolId || !weekId) return;
    getClassSubjectsByWeek(schoolId, weekId)
      .then((r) => {
        const cls = ((r && r.effective) || {})[classId] || {};
        setNeedMap(Object.fromEntries(Object.entries(cls).map(([sid, cfg]) => [sid, Number(cfg && cfg.periodsPerWeek) || 0])));
        setPccmMap(Object.fromEntries(Object.entries(cls).filter(([, cfg]) => cfg && cfg.assignedTeacherId).map(([sid, cfg]) => [sid, cfg.assignedTeacherId])));
      })
      .catch(() => setNeedMap({}));
  }, [schoolId, weekId, classId]);

  // Môn của lớp: đang có / cần (PCCM tuần này), môn thiếu tiết lên đầu.
  const subjectStats = useMemo(() => {
    const have = {};
    Object.entries(grid).forEach(([k, v]) => { if (k.startsWith(`${classId}|`) && Array.isArray(v) && v[0] != null) have[v[0]] = (have[v[0]] || 0) + 1; });
    // Liệt kê TẤT CẢ môn (kể cả HĐTN(SH)/HĐTN(CC)... không nằm trong PCCM lớp).
    const ids = new Set([...(needMap ? Object.keys(needMap) : []), ...Object.keys(have), ...Object.keys(subjectById)]);
    return [...ids].filter((sid) => subjectById[sid] && !["Lập trình Thiết kế Web", "Pháp luật"].includes(String(subjectById[sid]).normalize("NFC"))).map((sid) => {
      // Sinh hoạt (HĐTN(SH)) không nằm trong PCCM nhưng mỗi lớp luôn cần 1 tiết/tuần.
      const isSH = String(subjectById[sid]).normalize("NFC") === "HĐTN(SH)";
      const need = needMap ? (needMap[sid] || (isSH ? 1 : 0)) : null;
      const h = have[sid] || 0;
      return { sid, name: subjectById[sid], have: h, need, diff: need === null ? 0 : need - h };
    }).sort((a, b) => (b.diff > 0) - (a.diff > 0) || b.diff - a.diff || a.name.localeCompare(b.name, "vi"));
  }, [grid, classId, needMap, subjectById]);

  const info = useMemo(() => {
    const teachingAt = {}, classesOf = {}, khoisOf = {};
    Object.entries(grid).forEach(([k, v]) => {
      if (!Array.isArray(v) || !v[1] || typeof v[1] !== "string") return;
      const [cid, d, p] = k.split("|");
      (teachingAt[v[1]] = teachingAt[v[1]] || new Set()).add(`${d}|${p}`);
      (classesOf[v[1]] = classesOf[v[1]] || new Set()).add(cid);
      (khoisOf[v[1]] = khoisOf[v[1]] || new Set()).add(khoiOf(classById[cid]));
    });
    return { teachingAt, classesOf, khoisOf };
  }, [grid, classById]);

  const homeroomTid = (classes.find((c) => c.id === classId) || {}).homeroomTeacherId || null;
  const isHdtn = /^HĐTN/.test(String(subjectById[subjectSel] || "").normalize("NFC"));
  const candidates = useMemo(() => teachers
    .filter((t) => t.status !== "Đã nghỉ" && !NOT_TEACHING_NAMES.has(String(t.name || "").normalize("NFC").trim()))
    .filter((t) => !isBusyAt(t.id, day, period) && !(info.teachingAt[t.id] && info.teachingAt[t.id].has(`${day}|${period}`)))
    .map((t) => ({
      t,
      sameSubject: !!subjectSel && ((t.subjectIds || []).includes(subjectSel) || (isHdtn && !!homeroomTid && t.id === homeroomTid)), // HĐTN: GVCN lớp = cùng môn
      sameClass: !!(info.classesOf[t.id] && info.classesOf[t.id].has(classId)),
      sameKhoi: !!(info.khoisOf[t.id] && info.khoisOf[t.id].has(khoi)),
    })), [teachers, isBusyAt, day, period, info, subjectSel, classId, khoi, isHdtn, homeroomTid]);

  const shown = useMemo(() => {
    const qn = norm(q);
    return candidates
      .filter((c) => (filter === "subject" ? c.sameSubject : filter === "class" ? c.sameClass : filter === "khoi" ? c.sameKhoi : true))
      .filter((c) => !qn || norm(c.t.name).includes(qn))
      .sort((a, b) => (b.sameSubject - a.sameSubject) || (b.sameClass - a.sameClass) || (b.sameKhoi - a.sameKhoi) || a.t.name.localeCompare(b.t.name, "vi"));
  }, [candidates, filter, q]);

  const counts = {
    subject: candidates.filter((c) => c.sameSubject).length, class: candidates.filter((c) => c.sameClass).length,
    khoi: candidates.filter((c) => c.sameKhoi).length, all: candidates.length,
  };

  // Chọn GV không dạy môn đang chọn -> tự đổi sang môn của GV (ưu tiên môn lớp đang thiếu); chưa chọn môn -> lấy môn của GV.
  const pick = (t) => {
    setPicked(t.id);
    const own = (t.subjectIds || []).filter((sid) => subjectById[sid]);
    if (own.length === 0 || own.includes(subjectSel)) return;
    // Tiết HĐTN (Sinh hoạt/Chào cờ) do GVCN dạy, không gắn môn chuyên -> giữ nguyên, không tự đổi sang môn của GV (vd Lập trình Web).
    if (/^HĐTN/.test(String(subjectById[subjectSel] || "").normalize("NFC"))) return;
    const lackOf = (sid) => { const st = subjectStats.find((x) => x.sid === sid); return st && st.need !== null ? st.diff : 0; };
    setSubjectSel(own.slice().sort((a, b) => lackOf(b) - lackOf(a))[0]);
  };

  const save = async () => {
    setErr("");
    if (!subjectSel) { setErr("Chọn môn học."); return; }
    if (!picked) { setErr("Chọn 1 giáo viên trong danh sách bên dưới."); return; }
    setSaving(true);
    try {
      const r = await assignRunCell(schoolId, runId, { teacherId: picked, classId, subjectId: subjectSel, day, period, reason, overwrite: false });
      if (r && r.pending) window.alert(r.message || "Đã gửi yêu cầu sửa — chờ duyệt.");
      onSaved && onSaved(r);
    } catch (e) {
      setErr(e.message || "Lưu thất bại.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={box} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>Điền ô trống: {className} — {DAYS[day]} tiết {period + 1}</h3>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        <p style={{ margin: "6px 0 10px", color: "#6b7280", fontSize: 13 }}>Chọn môn và giáo viên rảnh để dạy lớp này ở giờ này (ghi thẳng vào TKB đang xem, không ghi vào xếp tay).</p>

        <div style={{ fontWeight: 700, fontSize: 13, color: "#6b7280", margin: "4px 0" }}>Môn học (số tiết có / cần của {className} tuần này)</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          {subjectStats.map((st) => {
            const lack = st.need !== null && st.diff > 0;
            const over = st.need !== null && st.diff < 0;
            const on = subjectSel === st.sid;
            return (
              <button key={st.sid} type="button" onClick={() => { setSubjectSel(st.sid); setPicked(""); }}
                style={{ padding: "4px 10px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600,
                  border: `1px solid ${on ? "#0ea5a3" : lack ? "#f59e0b" : "#d1d5db"}`, background: on ? "#0ea5a3" : lack ? "#fffbeb" : "#fff", color: on ? "#fff" : lack ? "#b45309" : "#374151" }}>
                {st.name}: {st.have}/{st.need === null ? "…" : st.need}{lack ? ` ⚠ thiếu ${st.diff}` : over ? ` ↑ dư ${-st.diff}` : st.need !== null ? " ✓ đủ" : ""}
              </button>
            );
          })}
        </div>
        {(subjectSel || homeroomTid) && (() => {
          // PCCM của môn đang chọn cho lớp này (tuần này; môn HĐTN = GVCN lớp) + GVCN lớp — luôn hiện kèm tình trạng
          // đúng giờ này (kể cả khi GV đó không rảnh nên không có trong danh sách bên dưới), nút chọn nhanh nếu rảnh.
          const statusOf = (tid) => {
            const t = teachers.find((x) => x.id === tid);
            if (t && t.status === "Đã nghỉ") return ["đã nghỉ việc", false];
            if (isBusyAt(tid, day, period)) return ["khai bận giờ này", false];
            if (info.teachingAt[tid] && info.teachingAt[tid].has(`${day}|${period}`)) {
              const k = Object.keys(grid).find((kk) => { const v = grid[kk]; const [, d, p] = kk.split("|"); return Array.isArray(v) && v[1] === tid && Number(d) === day && Number(p) === period; });
              return [`đang dạy ${k ? classById[k.split("|")[0]] || "lớp khác" : "lớp khác"} giờ này`, false];
            }
            return ["rảnh giờ này", true];
          };
          const pccmTid = subjectSel ? (pccmMap[subjectSel] || (isHdtn ? homeroomTid : null)) : null;
          const rows = [];
          if (subjectSel) rows.push({ label: <>📋 PCCM <b>{subjectById[subjectSel]}</b> lớp {className}{isHdtn && !pccmMap[subjectSel] ? " (GVCN)" : ""}</>, tid: pccmTid });
          if (homeroomTid && homeroomTid !== pccmTid) rows.push({ label: <>👩‍🏫 GVCN lớp {className}</>, tid: homeroomTid });
          return (
            <div style={{ margin: "0 0 10px", padding: "8px 10px", borderRadius: 8, fontSize: 13, background: "#f0f9ff", border: "1px solid #bae6fd", display: "grid", gap: 4 }}>
              {rows.map(({ label, tid }, i) => {
                const t = tid ? teachers.find((x) => x.id === tid) : null;
                const [status, free] = tid ? statusOf(tid) : ["", false];
                return (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span>{label}: {tid ? <b>{t ? t.name : tid}</b> : <i>chưa phân công</i>}</span>
                    {tid && <span style={{ color: free ? "#047857" : "#b45309" }}>— {status}</span>}
                    {free && picked !== tid && <button type="button" onClick={() => setPicked(tid)} style={{ padding: "2px 10px", border: "1px solid #0ea5a3", borderRadius: 6, background: "#fff", color: "#0f766e", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Chọn GV này</button>}
                    {free && picked === tid && <span style={{ color: "#047857", fontWeight: 600 }}>✓ đã chọn</span>}
                  </div>
                );
              })}
            </div>
          );
        })()}

        <div style={{ fontWeight: 700, fontSize: 13, color: "#6b7280", margin: "4px 0" }}>Giáo viên rảnh đúng giờ này (không khai bận, không dạy lớp khác)</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
          {FILTERS.map((f) => (<button key={f.key} type="button" onClick={() => setFilter(f.key)} style={filter === f.key ? chipOn : chip}>{f.label} ({counts[f.key]})</button>))}
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm theo tên giáo viên..." style={{ width: "100%", boxSizing: "border-box", padding: 6, border: "1px solid #d1d5db", borderRadius: 6, marginBottom: 6 }} />
        <div style={{ maxHeight: 240, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 6 }}>
          {shown.length === 0 ? (
            <p style={{ color: "#6b7280", fontSize: 13, padding: 10, margin: 0 }}>{subjectSel || filter !== "subject" ? "Không có giáo viên nào phù hợp bộ lọc này." : "Chọn môn ở trên, hoặc đổi bộ lọc sang \"Tất cả GV rảnh\"."}</p>
          ) : shown.map((c) => (
            <label key={c.t.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 10px", borderBottom: "1px solid #f3f4f6", cursor: "pointer", background: picked === c.t.id ? "#ecfdf5" : "#fff" }}>
              <input type="radio" name="fill-teacher" checked={picked === c.t.id} onChange={() => pick(c.t)} />
              <span style={{ fontWeight: 600 }}>{c.t.name}</span>
              <span style={{ color: "#6b7280", fontSize: 12 }}>{[c.t.specialty, c.t.employmentType].filter(Boolean).join(" · ")}</span>
              <span style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                {homeroomTid === c.t.id && <span style={tag("#be185d")}>GVCN</span>}
                {c.sameSubject && <span style={tag("#0f766e")}>cùng môn</span>}
                {c.sameClass && <span style={tag("#1d4ed8")}>dạy lớp này</span>}
                {c.sameKhoi && <span style={tag("#7c3aed")}>cùng khối</span>}
              </span>
            </label>
          ))}
        </div>

        <label style={{ display: "block", fontSize: 14, margin: "10px 0 0" }}>Lý do / ghi chú (không bắt buộc)
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="VD: dạy bù, dạy thay..." style={{ width: "100%", boxSizing: "border-box", marginTop: 4, padding: 6, border: "1px solid #d1d5db", borderRadius: 6 }} />
        </label>
        {err && <p style={{ color: "#dc2626", margin: "8px 0 0" }}>{err}</p>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <button type="button" onClick={onClose} disabled={saving} style={btnGhost}>Hủy</button>
          <button type="button" onClick={save} disabled={saving} style={btnPrimary}>{saving ? "Đang lưu..." : "Xác nhận điền tiết"}</button>
        </div>
      </div>
    </div>
  );
};

const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const box = { background: "#fff", borderRadius: 12, padding: 18, width: 640, maxWidth: "94vw", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 20px 40px rgba(0,0,0,.2)" };
const chip = { padding: "4px 10px", border: "1px solid #d1d5db", borderRadius: 999, background: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#374151" };
const chipOn = { ...chip, background: "#0ea5a3", borderColor: "#0ea5a3", color: "#fff" };
const tag = (color) => ({ fontSize: 11, padding: "1px 6px", borderRadius: 999, border: `1px solid ${color}`, color });
const btnGhost = { padding: "8px 14px", border: "1px solid #d1d5db", background: "#fff", borderRadius: 6, cursor: "pointer", fontWeight: 600 };
const btnPrimary = { padding: "8px 16px", border: "none", background: "#0ea5a3", color: "#fff", borderRadius: 6, cursor: "pointer", fontWeight: 700 };

export default HappySchoolCellFillDialog;
